// apps/landing-page/src/components/payment/QrisTeaser.tsx

import { QrCode } from 'lucide-react';
import { useTranslations } from 'next-intl';
import type { PaymentMethodResponse } from '@/lib/api/payments';

/**
 * Trust badge shown near a price on product pages (test/course detail).
 * Conditional on the real QRIS payment method entry (category === 'QRIS')
 * so it disappears the moment that's no longer true, instead of a static
 * string that could silently go stale if QRIS ever got a non-zero fee or
 * was toggled off.
 *
 * Does NOT fetch anything itself — `qrisMethod` is expected to come from a
 * single `usePaymentMethods()` call at the page level, not one fetch per
 * card. Mirrors apps/dashboard/src/components/payment/QrisTeaser.tsx — keep
 * the two in sync.
 *
 * No hover state: this is informational text, not a control (see
 * docs/DESIGN_GUIDELINES.md — "if it hovers, it clicks").
 */
export function QrisTeaser({
  price,
  qrisMethod,
  className = '',
}: {
  price: number;
  qrisMethod: PaymentMethodResponse | null;
  className?: string;
}) {
  const t = useTranslations('common');

  if (!qrisMethod?.isActive) return null;
  if (qrisMethod.minAmount != null && price < qrisMethod.minAmount) return null;

  return (
    <p className={`flex items-center gap-1 text-[11px] text-slate-500 ${className}`}>
      <QrCode className="w-3 h-3 text-slate-400 shrink-0" />
      <span>{t('qrisTeaser')}</span>
    </p>
  );
}
